import { Badge, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { IconArrowLeft, IconDownload, IconSend } from '@tabler/icons-react'
import { useMemo } from 'react'
import type { CartItem } from './OrderCart'
import { SUBMIT_PERMISSION_TOOLTIP, SIGNATURE_REQUIRED_TOOLTIP } from './constants'
import { TooltipButton } from './TooltipButton'

export type ReviewSection = {
  title: string
  items: { label: string; value: string }[]
}

type Props = {
  vendorName: string
  tests: CartItem[]
  diagnosisText: string
  sections: ReviewSection[]
  signatureDataUrl: string
  signatureDate: string
  onEdit: () => void
  onSubmit: () => void
  onPreview: () => void
  submitting: boolean
  exporting: boolean
  orderSubmitted: boolean
  isPractitioner: boolean
  canOrderLoading?: boolean
  labError?: string | null
  orgError?: string | null
  /** PD-1312 — overrides the submit label. A patient-carried requisition isn't
   *  transmitted anywhere, so "Submit Order" overstates what pressing it does. */
  submitLabel?: string
  /** PD-1312 — label once submitted, paired with `submitLabel`. */
  submittedLabel?: string
  /** PD-775 — the order cannot be faxed anywhere yet (no facility, a facility
   *  awaiting admin approval, or one with no fax on file), so submitting could
   *  only fail. Offers Save as Draft in Submit's place, the way Radiology has
   *  always done. */
  mustSaveAsDraft?: boolean
  /** PD-775 — why submitting is unavailable, shown until the draft is saved. */
  draftNotice?: string | null
  /** PD-775 — required whenever `mustSaveAsDraft` is set. */
  onSaveDraft?: () => void
}

export const OrderReviewSummary = ({
  vendorName,
  tests,
  diagnosisText,
  sections,
  signatureDataUrl,
  signatureDate,
  onEdit,
  onSubmit,
  onPreview,
  submitting,
  exporting,
  orderSubmitted,
  isPractitioner,
  canOrderLoading = false,
  labError,
  orgError,
  submitLabel,
  submittedLabel,
  mustSaveAsDraft = false,
  draftNotice,
  onSaveDraft,
}: Props) => {
  const testsByCategory = useMemo(() => {
    const grouped: Record<string, CartItem[]> = {}
    for (const test of tests) {
      const cat = test.category || 'Tests'
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(test)
    }
    return grouped
  }, [tests])

  const effectivePractitioner = canOrderLoading ? true : isPractitioner
  const isSubmitDisabled =
    orderSubmitted || !effectivePractitioner || !signatureDataUrl || !!labError || !!orgError
  const submitDisabledReason = !effectivePractitioner
    ? SUBMIT_PERMISSION_TOOLTIP
    : !signatureDataUrl
      ? SIGNATURE_REQUIRED_TOOLTIP
      : labError
        ? labError
        : orgError
          ? orgError
          : null

  // Saving a draft is still the practitioner's signed act, so it carries the
  // same permission and signature gates as submitting — but not the lab/org
  // errors, which only matter to an order actually being transmitted.
  const isDraftDisabled = orderSubmitted || !effectivePractitioner || !signatureDataUrl
  const draftDisabledReason = !effectivePractitioner
    ? SUBMIT_PERMISSION_TOOLTIP
    : !signatureDataUrl
      ? SIGNATURE_REQUIRED_TOOLTIP
      : null

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="md">
          ORDER SUMMARY — {vendorName}
        </Text>

        {/* Selected tests */}
        {tests.length > 0 && (
          <>
            <Text fw={500} size="sm" mb="xs">
              Selected Tests ({tests.length})
            </Text>
            <Stack gap="xs" mb="md">
              {Object.entries(testsByCategory).map(([category, categoryTests]) => (
                <div key={category}>
                  <Text size="xs" fw={600} c="dimmed">
                    {category}
                  </Text>
                  <Stack gap={4} mt={2}>
                    {categoryTests.map((test) => (
                      <div key={test.code}>
                        <Badge size="sm" variant="light" color="blue">
                          {test.name}
                        </Badge>
                        {test.subItems && test.subItems.length > 0 && (
                          <Stack gap={2} ml="md" mt={2}>
                            {test.subItems.map((sub) => (
                              <Text key={sub.code} size="xs" c="dimmed">
                                {sub.name}
                              </Text>
                            ))}
                          </Stack>
                        )}
                      </div>
                    ))}
                  </Stack>
                </div>
              ))}
            </Stack>
          </>
        )}

        {/* Diagnosis codes */}
        {diagnosisText && (
          <>
            <Text fw={500} size="sm" mt="md" mb="xs">
              Diagnosis Codes
            </Text>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
              {diagnosisText}
            </Text>
          </>
        )}

        {/* Vendor-specific sections */}
        {sections.map((section) => {
          const nonEmpty = section.items.filter((i) => i.value)
          if (nonEmpty.length === 0) return null
          return (
            <div key={section.title}>
              <Text fw={500} size="sm" mt="md" mb="xs">
                {section.title}
              </Text>
              <Stack gap={2}>
                {nonEmpty.map((item) => (
                  <Group key={item.label} gap="xs">
                    <Text size="xs" c="dimmed" w={160}>
                      {item.label}:
                    </Text>
                    <Text size="xs">{item.value}</Text>
                  </Group>
                ))}
              </Stack>
            </div>
          )
        })}

        {/* Signature */}
        {signatureDataUrl && (
          <>
            <Text fw={500} size="sm" mt="md" mb="xs">
              Provider Signature
            </Text>
            <img
              src={signatureDataUrl}
              alt="Provider signature"
              className="h-12 rounded border border-gray-200 p-1"
            />
            <Text size="xs" c="dimmed">
              Signed: {signatureDate}
            </Text>
          </>
        )}
      </Paper>

      {mustSaveAsDraft && draftNotice && !orderSubmitted && (
        <Text size="sm" c="orange">
          {draftNotice}
        </Text>
      )}

      <Group justify="space-between" pb="xl">
        <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={onEdit}>
          Back to Edit
        </Button>
        <Group gap="sm">
          <Button
            variant="outline"
            leftSection={<IconDownload size={16} />}
            onClick={onPreview}
            loading={exporting}
          >
            Download PDF
          </Button>
          {mustSaveAsDraft ? (
            <TooltipButton
              color="blue"
              loading={submitting}
              disabled={isDraftDisabled}
              disabledReason={draftDisabledReason}
              onClick={onSaveDraft}
            >
              {orderSubmitted ? 'Draft Saved' : 'Save as Draft'}
            </TooltipButton>
          ) : (
            <TooltipButton
              color="teal"
              leftSection={<IconSend size={16} />}
              loading={submitting}
              disabled={isSubmitDisabled}
              disabledReason={submitDisabledReason}
              onClick={onSubmit}
            >
              {orderSubmitted
                ? (submittedLabel ?? 'Order Submitted')
                : (submitLabel ?? 'Submit Order')}
            </TooltipButton>
          )}
        </Group>
      </Group>
    </Stack>
  )
}
